/**
 * BatchAddInvestmentsModal — lets a user add up to 10 investments of the SAME
 * type in one flow. Type is picked once at the top, then a stack of investment
 * cards is shown. Each card has the same full form as the single-add modal.
 *
 * Submit posts the whole batch atomically: backend validates ALL rows first,
 * and if anything fails NOTHING is inserted (row errors surface inline).
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { INVESTMENT_TYPES } from './investmentConstants';
import { getInitialFormData } from './investmentFormData';
import { autoCalculateFields } from '../investmentFormUtils';
import { useInvestmentFormConfig } from './useInvestmentFormConfig';
import { useBatchAddInvestments } from '../hooks/useBatchAddInvestments';
import BatchInvestmentRow from './BatchInvestmentRow';

const MAX_ROWS = 10;
const newRow = (type) => ({ ...getInitialFormData(null), type });

const BatchAddInvestmentsModal = ({ isOpen, onClose, onSuccess }) => {
  const [type, setType] = useState('');
  const [rows, setRows] = useState([]);
  const { config, setConfig, entities, relatedAssets } = useInvestmentFormConfig(isOpen);
  const { isLoading, rowErrors, submitBatch } = useBatchAddInvestments(() => {
    onSuccess?.();
    handleClose(true);
  });

  const reset = () => { setType(''); setRows([]); };

  const hasUserData = useMemo(
    () => rows.some((r) => r.name?.trim() || (r.amount && Number(r.amount) > 0) || r.notes?.trim()),
    [rows],
  );

  const handleClose = (force = false) => {
    if (!force && hasUserData) {
      // Only prompt if the user has actually typed something.
      if (!window.confirm('Discard your unsaved entries?')) return;
    }
    reset();
    onClose?.();
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    setRows([newRow(newType)]);
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? autoCalculateFields(r, field, value) : r));
  };
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const addRow = () => {
    if (rows.length >= MAX_ROWS) {
      toast.info(`You can add up to ${MAX_ROWS} investments per batch.`);
      return;
    }
    setRows((prev) => [...prev, newRow(type)]);
  };

  const incompleteIdxs = useMemo(
    () => rows
      .map((r, i) => (!r.name?.trim() || !r.type || !(Number(r.amount) > 0)) ? i : -1)
      .filter((i) => i >= 0),
    [rows],
  );
  const incomplete = incompleteIdxs.length;

  const onSubmit = async () => {
    if (incomplete > 0) {
      const list = incompleteIdxs.map((i) => `#${i + 1}`).join(', ');
      toast.error(`Please complete name and amount (>0) on entries ${list}.`);
      return;
    }
    await submitBatch(rows);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="batch-add-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-amber-600" />
            Add multiple investments
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            Pick the investment type once, then add up to {MAX_ROWS} entries in one go. We&rsquo;ll save them all together &mdash; if any row has an error, nothing is saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="portfolio-input-label">
              Investment Type <span className="text-red-500">*</span>
            </Label>
            <Select value={type} onValueChange={handleTypeChange} disabled={rows.length > 0}>
              <SelectTrigger className="portfolio-input" data-testid="batch-type-select">
                <SelectValue placeholder="Select the type shared by all entries" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INVESTMENT_TYPES).map(([k, lbl]) => (
                  <SelectItem key={k} value={k}>{lbl}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rows.length > 0 && (
              <button type="button" onClick={reset}
                className="text-xs text-slate-500 underline hover:text-slate-700"
                data-testid="batch-change-type">
                Change type (clears all entries)
              </button>
            )}
          </div>

          {rows.length === 0 && (
            <div className="text-center py-10 text-slate-500 border border-dashed border-slate-300 rounded-lg">
              <p className="text-sm">Pick an investment type to start adding entries.</p>
            </div>
          )}

          {rows.map((row, idx) => (
            <BatchInvestmentRow
              key={idx}
              index={idx} rowData={row}
              onFieldChange={(f, v) => updateRow(idx, f, v)}
              onRemove={removeRow} canRemove={rows.length > 1}
              config={config} setConfig={setConfig}
              entities={entities} relatedAssets={relatedAssets}
              errors={rowErrors[idx] ? { _row: rowErrors[idx] } : {}}
              isIncomplete={incompleteIdxs.includes(idx)}
            />
          ))}

          {type && rows.length < MAX_ROWS && (
            <Button
              type="button" variant="outline" onClick={addRow}
              className="w-full border-dashed border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="batch-add-row"
            >
              <Plus className="w-4 h-4 mr-1" /> Add another entry ({rows.length}/{MAX_ROWS})
            </Button>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <span className="text-xs text-slate-500">
              {rows.length === 0 ? '' : `${rows.length} entr${rows.length === 1 ? 'y' : 'ies'} ready · ${incomplete} incomplete`}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => handleClose()} data-testid="batch-cancel">
                Cancel
              </Button>
              <Button
                type="button" onClick={onSubmit}
                disabled={isLoading || rows.length === 0 || incomplete > 0}
                className="portfolio-button-primary"
                data-testid="batch-save-all"
              >
                {isLoading ? 'Saving…' : `Save all (${rows.length})`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchAddInvestmentsModal;
